import { Component, ElementRef, Input, OnDestroy, EventEmitter, Output } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subscription  } from 'rxjs';

import { ApiError, AuthService, DataTablesService, MessageService, VisualizacaoStorageService, BaseVisualizacaoService } from '../../../../../core';
import { LeadFilter } from '../../filter';
import { LeadSearchOrderBy } from '../../lead-search-orderby';
import { LeadSearch } from '../../lead-search';
import { LeadSearchTotal } from '../../lead-search-total';
import { LeadOptions, LeadOptionsActions } from '../../lead-options';
import { LeadAuthGuardService, LeadSearchHttpService } from '../../services';
import { TemperaturaPipe, TemperaturaImagePipe } from '../../../../common/temperatura/pipes';
import { OrigemPipe, OrigemIconPipe } from '../../../../common/origens/pipes';
import { CanalMidiaSocialPipe, CanalMidiaSocialIconPipe } from '../../../../common/canal-midia-social/pipes';

@Component({
    selector: 'sgt-lead-table',
    templateUrl : './lead-table.component.html',
    styleUrls: ['./lead-table.component.less'],
    providers: [
        OrigemPipe,
        OrigemIconPipe,
        CanalMidiaSocialPipe,
        CanalMidiaSocialIconPipe,
        TemperaturaPipe,
        TemperaturaImagePipe
    ]
})
export class LeadTableComponent implements OnDestroy {
    @Input() visualizacaoStorageKey = 'leadTableVisualizacao';
    @Input() optionsVisibility: LeadOptionsActions = {
        canEdit: true,
        canDelete: true,
        canInsertNegocio: true
    }
    @Input() filter: LeadFilter;

    @Output() edit = new EventEmitter<LeadSearch>();
    @Output() insertNegocio = new EventEmitter<LeadSearch>();
    @Output() delete = new EventEmitter<LeadSearch>();

    // Total
    isLoadingTotal: boolean;
    total: LeadSearchTotal;

    private dataTable: any;
    /** Subscriptions das pesquisas. Utilizado para realizar o unsubscribe e cancelar o retorno de requests de pesquissa pendentes antes de iniciar novos requests */
    private searchSubs$: Subscription;

    constructor(private el: ElementRef,
                private dataTablesService: DataTablesService,
                private messageService: MessageService,
                private visualizacaoStorageService: VisualizacaoStorageService,
                private leadAuthGuardService: LeadAuthGuardService,
                private leadSearchHttpService: LeadSearchHttpService,
                private origemPipe: OrigemPipe,
                private origemIconPipe: OrigemIconPipe,
                private canalMidiaSocialPipe: CanalMidiaSocialPipe,
                private canalMidiaSocialIconPipe: CanalMidiaSocialIconPipe,
                private temperaturaPipe: TemperaturaPipe,
                private temperaturaImagePipe: TemperaturaImagePipe) { }

    search() {
        // Realizando unsubscribe dos subscriptions atuais e criando um novo para as próximas pesquisas
        this.searchSubs$?.unsubscribe();
        this.searchSubs$ = new Subscription();

        // Obtendo os totais de leads
        this.getTotais()
            .then(() => {
                if (!this.dataTable) { // Table ainda não criada
                    // Obtendo os dados da datatable armazenados no storage para sabermos o order e posição do scroll
                    const vs = this.visualizacaoStorageService.get(this.visualizacaoStorageKey);

                    this.createTable(vs?.order)
                        .then(() => {
                            // Realizando scroll para a posição inicial salva
                            if (vs?.scrollerStartPosition > this.dataTable?.scroller?.page()?.end) {
                                this.dataTable?.scroller?.toPosition(vs?.scrollerStartPosition);
                            }
                        });
                } else { // Table já criada, somente damos um reload
                    this.dataTable.ajax.reload();
                }
            })
            .catch(() => {});
    }

    /** Obtem os totais dos leads */
    private getTotais(): Promise<LeadSearchTotal> {
        return new Promise<LeadSearchTotal>((resolve, reject) => {
            this.searchSubs$.add(this.leadSearchHttpService.getTotais(this.filter)
                .subscribe(
                    total => {
                        this.total = total;
                        this.isLoadingTotal = false;
                        resolve(total);
                    },
                    (error: ApiError) => {
                        this.isLoadingTotal = false;
                        this.messageService.error(error.message, 'Erro ao obter os totais dos leads', error);
                        reject();
                    }
                )
            );

        })
    }

    /** Cria a dataTable dos leads */
    private createTable(initOrder: any[] = null): Promise<void> {
        return new Promise<void>((resolve) => {
            const columns: any[] = [
                {
                    data: null,
                    className: 'col-options',
                    orderable: false,
                    render: (data, type, row, meta) => {
                        const entity = data as LeadSearch;

                        // Opções
                        const options = new LeadOptions();
                        // Editar
                        options.canEdit = this.optionsVisibility.canEdit && this.leadAuthGuardService.canEdit(entity);
                        // Excluir
                        options.canDelete = this.optionsVisibility.canDelete && this.leadAuthGuardService.canDelete();
                        // Inserir um negócio
                        options.canInsertNegocio = this.optionsVisibility.canInsertNegocio;

                        let template = '';
                        if (options.hasAnyOption()) {
                            template += `
                                <span class="btn-group dropup table-options">
                                    <button type="button" title="Opções" class="btn btn-link btn-options dropdown-toggle" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
                                        <i class="fas fa-ellipsis-h" aria-hidden="true"></i>
                                    </button>
                                    <div class="dropdown-menu">
                            `;
                            // Editar
                            if (options.canEdit) {
                                template +=  `
                                    <a class="dropdown-item" id="edit">
                                        <i class="fas fa-pencil-alt" aria-hidden="true"></i> Editar
                                    </a>
                                `;
                            }

                            // Inserir negócio
                            if (options.canInsertNegocio) {
                                template +=  `
                                    <div class="dropdown-divider"></div>
                                    <a class="dropdown-item" id="insertNegocio">
                                        <i class="fas fa-bullseye" aria-hidden="true"></i> Criar Qualificação
                                    </a>
                                `;
                            }

                            // Excluir
                            if (options.canDelete) {
                                template +=  `
                                    <div class="dropdown-divider"></div>
                                    <a class="dropdown-item dropdown-item-delete" id="delete">
                                        <i class="far fa-trash-alt" aria-hidden="true"></i> Excluir
                                    </a>
                                `;
                            }

                            template += `
                                    </div>
                                </span>
                            `;
                        }

                        return template;
                    },
                    createdCell: (td, cellData, rowData, row, col) => {
                        const dropdown = jQuery(td).find('.btn-group');
                        // Ao abrir o dropdown, adicionamos ele no body para exibi-lo corretamente na table
                        dropdown.on('show.bs.dropdown', function() {
                            jQuery('body').append(dropdown.css({
                                    position: 'absolute',
                                    left: dropdown.offset().left,
                                    top: dropdown.offset().top
                                }).detach());
                        });
                        // Ai fechar o dropdown, adicionamos ele novamente a coluna das opções na table
                        dropdown.on('hidden.bs.dropdown', function() {
                            jQuery(td).append(dropdown.css({
                                position: 'relative',
                                left: 'auto',
                                top: 'auto'
                            }).detach());
                        });

                        // Vinculando os eventos de click das opções da tabela
                        const myself = this;
                        // Opções do dropdown
                        dropdown.on('click', 'a', function() {
                            if (this.id === 'edit') {
                                myself.edit.emit(cellData);
                            } else if (this.id === 'insertNegocio') {
                                myself.insertNegocio.emit(cellData);
                            } else if (this.id === 'delete') {
                                myself.delete.emit(cellData);
                            }
                        });
                    }
                },
                {
                    data: 'nome',
                    className: 'col-nome',
                    serverOrderByColumn: LeadSearchOrderBy.Nome,
                    createdCell: (td, cellData, rowData, row, col) => {
                        if (cellData) {
                            td.title = cellData;
                        }
                    }
                },
                {
                    data: 'origem',
                    className: 'col-origem',
                    serverOrderByColumn: LeadSearchOrderBy.Origem,
                    render: (data: number) => {
                        if (data) {
                            return `
                                <i class="${this.origemIconPipe.transform(data)}"></i>
                                <span>${this.origemPipe.transform(data)}</span>
                            `;
                        }
                        return '';
                    },
                    createdCell: (td, cellData, rowData, row, col) => {
                        if (cellData) {
                            td.title = this.origemPipe.transform(cellData);
                        }
                    }
                },
                {
                    data: 'nomeCampanhaOrigem',
                    className: 'col-nome-campanha',
                    serverOrderByColumn: LeadSearchOrderBy.NomeCampanhaOrigem,
                    createdCell: (td, cellData, rowData, row, col) => {
                        if (cellData) {
                            td.title = cellData;
                        }
                    }
                },
                {
                    data: 'nomeCampanhaQualificacao',
                    className: 'col-nome-campanha',
                    serverOrderByColumn: LeadSearchOrderBy.NomeCampanhaQualificacao,
                    createdCell: (td, cellData, rowData, row, col) => {
                        if (cellData) {
                            td.title = cellData;
                        }
                    }
                },
                {
                    data: 'canalMidiaSocial',
                    className: 'col-canal-midia-social',
                    serverOrderByColumn: LeadSearchOrderBy.CanalMidiaSocial,
                    render: (data: number) => {
                        if (data) {
                            return `
                                <i class="${this.canalMidiaSocialIconPipe.transform(data)}"></i>
                                <span>${this.canalMidiaSocialPipe.transform(data)}</span>
                            `;
                        }
                        return '';
                    },
                    createdCell: (td, cellData, rowData, row, col) => {
                        if (cellData) {
                            td.title = this.canalMidiaSocialPipe.transform(cellData);
                        }
                    }
                },
                {
                    data: 'temperatura',
                    className: 'col-temperatura',
                    serverOrderByColumn: LeadSearchOrderBy.Temperatura,
                    render: (data: number) => {
                        if (data) {
                            return `
                                <img class="temperatura" src="${this.temperaturaImagePipe.transform(data)}" alt="${this.temperaturaPipe.transform(data)}" title="${this.temperaturaPipe.transform(data)}">
                                <span>${this.temperaturaPipe.transform(data)}</span>
                            `;
                        }
                        return '';
                    },
                    createdCell: (td, cellData, rowData, row, col) => {
                        if (cellData) {
                            td.title = this.temperaturaPipe.transform(cellData);
                        }
                    }
                },
                {
                    data: 'email',
                    className: 'col-email',
                    serverOrderByColumn: LeadSearchOrderBy.Email,
                    createdCell: (td, cellData, rowData, row, col) => {
                        if (cellData) {
                            td.title = cellData;
                        }
                    }
                },
                {
                    data: 'fone',
                    className: 'col-telefone',
                    serverOrderByColumn: LeadSearchOrderBy.Telefone,
                    createdCell: (td, cellData, rowData, row, col) => {
                        if (cellData) {
                            td.title = cellData;
                        }
                    }
                },
                {
                    data: 'celular',
                    className: 'col-celular',
                    serverOrderByColumn: LeadSearchOrderBy.Celular,
                    createdCell: (td, cellData, rowData, row, col) => {
                        if (cellData) {
                            td.title = cellData;
                        }
                    }
                },
                {
                    data: 'nomeCidade',
                    className: 'col-nome-cidade',
                    serverOrderByColumn: LeadSearchOrderBy.NomeCidade,
                    createdCell: (td, cellData, rowData, row, col) => {
                        if (cellData) {
                            td.title = cellData;
                        }
                    }
                },
                {
                    data: 'ufEstado',
                    className: 'col-uf-estado',
                    serverOrderByColumn: LeadSearchOrderBy.UFEstado,
                    createdCell: (td, cellData, rowData, row, col) => {
                        if (cellData) {
                            td.title = cellData;
                        }
                    }
                },
                {
                    data: 'nomeUsuarioResponsavel',
                    className: 'col-nome-usuario-responsavel',
                    serverOrderByColumn: LeadSearchOrderBy.NomeUsuarioResponsavel,
                    createdCell: (td, cellData, rowData, row, col) => {
                        if (cellData) {
                            td.title = cellData;
                        }
                    }
                }
            ];

            const columnDefs: any[] = [
                {orderable: false, targets: [0]}
            ];

            this.dataTablesService.showTable('#leadTable', this.dataTable, {
                columnDefs,
                columns,
                order: initOrder ? initOrder : [[1, 'asc']], // nome asc
                searching: false,
                info: false,
                serverSide: true,
                processing: true,
                deferRender: true,
                scroller: true,
                scrollX: true,
                // stateSave: true,
                ajax: (data, callback, settings) => {
                    // Order By
                    const order = data?.order[0];
                    const orderColumn = columns[order?.column];
                    const orderBy = orderColumn?.serverOrderByColumn;
                    const orderByDir = order?.dir;
                    // Limit
                    const limit = data.length;
                    // Offset
                    const offset = data.start;

                    this.searchSubs$.add(this.leadSearchHttpService.search(this.filter, orderBy, orderByDir, limit, offset)
                        .subscribe(
                            entities => {
                                callback({recordsTotal: this.total?.quantidadeLeads, recordsFiltered: this.total?.quantidadeLeads, data: entities});
                            },
                            (error: ApiError) => {
                                this.messageService.error(error.message, 'Erro ao pesquisar os leads', error);
                                callback({recordsTotal: 0, recordsFiltered: 0, data: []});
                            }
                        )
                    );
                }
            }).then(dataTable => {
                this.dataTable = dataTable;

                setTimeout(() => {
                    // Adicionando evento handler no scroll do body da datatable
                    jQuery(this.el.nativeElement).find('.dataTables_scrollBody').on('scroll', () => {
                        this.visualizacaoStorageService.set(null, null, this.dataTable?.page?.info()?.page, this.dataTable?.order(), this.dataTable?.scroller?.page()?.start, this.visualizacaoStorageKey);
                    });

                    resolve();
                }, 500);
            })
            .catch(() => {});
        });
    }

    /** Atualizando a pesquisa */
    refresh() {
        // Atualizando a pesquisa através da datatable
        this.dataTable?.draw(false);
        // Atualizando os totais
        this.getTotais()
            .then(() => {})
            .catch(() => {});
    }

    ngOnDestroy() {
        this.searchSubs$?.unsubscribe();

        // Removendo evento de scroll do body da datatable
        jQuery(this.el.nativeElement).find('.dataTables_scrollBody').off('scroll');
    }
}
